import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Card,
  EmptyState,
  HelpTip,
  KpiCard,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { api } from '@/lib/api';

import { formatDataHoraSegundos } from '@/lib/format-date';
/**
 * 🔴 Produtos que sumiram do Omie — a tela que a rodada URGENTE deixou pendente.
 *
 * ── O que aconteceu (BUGS #217, 04/09/2026)
 *
 * **960 produtos do v2 não existiam mais no Omie**, e o orçamentista continuava
 * vendo todos: o sync só acrescentava, nunca removia. O mesmo modelo aparecia
 * com dois preços — R$ 1.733,90 e R$ 2.890,00 — e nada dizia qual valia.
 *
 * O conserto subiu no mesmo dia: a varredura marca quem o Omie não devolve
 * mais, e a busca do orçamento deixa de oferecer. ⚠️ **Marca, não apaga** —
 * produto sumido pode estar num orçamento antigo, e apagá-lo quebraria o
 * histórico.
 *
 * ── ⚠️ Por que a tela só chega agora
 *
 * O relatório daquela rodada registrou: *"as rotas existem, e a rodada A está
 * reorganizando Configurações › Integrações — construir tela lá agora seria
 * colidir de propósito."* **É esta a rodada**, e é aqui que ela entra.
 */

interface PorEmpresa {
  empresa_id: string;
  sumidos: number;
  total: number;
  ultima_varredura: string | null;
}

interface ProdutoSumido {
  empresa_id: string;
  codigo: string;
  descricao: string;
  preco_unitario: number | null;
  sumiu_em_iso: string | null;
}

interface Resposta {
  porEmpresa: PorEmpresa[];
  lista: ProdutoSumido[];
}

const AJUDA =
  'A varredura pergunta ao Omie quais produtos ainda existem e marca os que ele não devolve mais. ' +
  'Marcar não apaga: o produto some da busca do orçamento, mas continua nos orçamentos antigos que ' +
  'já o usaram. Se ele voltar a existir no Omie, a varredura seguinte o desmarca sozinha.';

const empresaCurta = (id: string) => id.replace('empresa-igm-', '').toUpperCase();

function brl(v: number | null): string {
  if (v === null || Number.isNaN(v)) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function quando(iso: string | null): string {
  if (!iso) return 'nunca';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'nunca' : formatDataHoraSegundos(d);
}

export function ProdutosSumidosPage() {
  const [dados, setDados] = useState<Resposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [varrendo, setVarrendo] = useState(false);
  const [resultado, setResultado] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    setErro(null);
    api
      .get<Resposta>('/api/v1/admin/produtos/sumidos')
      .then(setDados)
      .catch((e: Error) => setErro(e.message));
  }, [tentativa]);

  async function varrer() {
    setVarrendo(true);
    setResultado(null);
    try {
      const r = await api.post<{ detalhe: string }>('/api/v1/admin/produtos/varrer-sumidos');
      setResultado(r.detalhe);
      setTentativa((t) => t + 1);
    } catch (e) {
      // 🔴 O formulário não é surdo: a recusa diz o que houve, não some.
      setResultado(`Não foi possível varrer: ${(e as Error).message}`);
    } finally {
      setVarrendo(false);
    }
  }

  const totalSumidos = (dados?.porEmpresa ?? []).reduce((a, e) => a + Number(e.sumidos ?? 0), 0);

  return (
    <section className="min-w-0 space-y-6" data-testid="produtos-sumidos-page">
      <PageHeader
        className="mb-0"
        crumb="Configurações · Integrações"
        title="Produtos que sumiram do Omie"
        subtitle="Produtos que existem no catálogo do sistema mas o Omie não devolve mais. Eles deixam de aparecer na busca do orçamento — e continuam nos orçamentos antigos."
      />

      {erro && (
        <Alert variant="danger" rail title="Não consegui carregar">
          {erro}{' '}
          <button type="button" className="underline" onClick={() => setTentativa((t) => t + 1)}>
            Tentar de novo
          </button>
        </Alert>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard label="Escondidos da busca" value={String(totalSumidos)} context="Nas duas empresas" />
        {(dados?.porEmpresa ?? []).map((e) => (
          <KpiCard
            key={e.empresa_id}
            label={`${empresaCurta(e.empresa_id)} — no catálogo`}
            value={String(e.total)}
            context={`${e.sumidos} escondidos · última varredura ${quando(e.ultima_varredura)}`}
          />
        ))}
      </div>

      <Card className="p-4 sm:p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5">
            <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
              Conferir agora contra o Omie
            </span>
            <HelpTip texto={AJUDA} />
          </span>
          <Button variant="secondary" onClick={() => void varrer()} disabled={varrendo}>
            {varrendo ? 'Conferindo…' : 'Conferir agora'}
          </Button>
        </div>
        <p className="text-xs text-ink-3 dark:text-ink-dark-3">
          A conferência lê o catálogo inteiro do Omie. Se a leitura vier curta demais, ela{' '}
          <strong>recusa marcar</strong> — leitura falha esconderia produto bom da busca, que é o
          oposto do que isto existe para fazer.
        </p>
        {resultado && (
          <Alert variant="info" rail data-testid="varredura-resultado">
            {resultado}
          </Alert>
        )}
      </Card>

      <Card className="p-4 sm:p-5">
        <h2 className="mb-3 text-sm font-medium text-ink-1 dark:text-ink-dark-1">
          Os que estão escondidos
        </h2>
        {!dados ? (
          <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
        ) : dados.lista.length === 0 ? (
          <EmptyState
            title="Nenhum produto escondido"
            description="Tudo o que está no catálogo do sistema também está no Omie."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead className="text-right">Preço no cache</TableHead>
                    <TableHead>Escondido em</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dados.lista.map((p) => (
                    <TableRow key={`${p.empresa_id}|${p.codigo}`}>
                      <TableCell>{empresaCurta(p.empresa_id)}</TableCell>
                      <TableCell className="tabular-nums">{p.codigo}</TableCell>
                      <TableCell>{p.descricao}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {brl(p.preco_unitario)}
                      </TableCell>
                      <TableCell>{quando(p.sumiu_em_iso)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {dados.lista.length >= 200 && (
              <Alert variant="info" rail className="mt-3">
                A lista mostra os 200 de maior preço. O número acima é o total.
              </Alert>
            )}
          </>
        )}
      </Card>
    </section>
  );
}
